import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { AppLayout } from '../components/AppLayout';
import { RequireAuth } from '../components/RequireAuth';
import { ApiKeysPage } from '../features/admin/pages/ApiKeysPage';
import { CampanasPage as AnunciosPage } from '../features/admin/pages/CampanasPage';
import { ConfiguracionAdminPage } from '../features/admin/pages/ConfiguracionAdminPage';
import { ErroresAdminPage } from '../features/admin/pages/ErroresAdminPage';
import { MetricasAdminPage } from '../features/admin/pages/MetricasAdminPage';
import { SistemaAdminPage } from '../features/admin/pages/SistemaAdminPage';
import { UsuariosPage } from '../features/admin/pages/UsuariosPage';
import { CambiarPasswordPage } from '../features/auth/pages/CambiarPasswordPage';
import { LoginPage } from '../features/auth/pages/LoginPage';
import { BandejaPage } from '../features/bandeja/pages/BandejaPage';
import { EmbudoPage } from '../features/bandeja/pages/EmbudoPage';
import { CampanaDetallePage } from '../features/campanas/pages/CampanaDetallePage';
import { CampanaEditorPage } from '../features/campanas/pages/CampanaEditorPage';
import { CampanasPage } from '../features/campanas/pages/CampanasPage';
import { ConexionesPage } from '../features/conexiones/pages/ConexionesPage';
import { InicioPage } from '../features/inicio/InicioPage';
import { MiPerfilPage } from '../features/perfil/pages/MiPerfilPage';
import { PublicarPage } from '../features/publicar/pages/PublicarPage';
import { TareasPage } from '../features/tareas/pages/TareasPage';

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/cambiar-password',
    element: (
      <RequireAuth>
        <CambiarPasswordPage />
      </RequireAuth>
    ),
  },
  // La home es el panel (ciclo-panel, R1): la única página que se le da a Ailen.
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout>
          <InicioPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  { path: '/inicio', element: <Navigate to="/" replace /> },
  {
    // El Publicador de contenido (ciclo-panel, R4): Ailen y Javier.
    path: '/publicar',
    element: (
      <RequireAuth roles={['ADMINISTRADOR', 'COLABORADOR']}>
        <AppLayout>
          <PublicarPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  // ciclo-emisor, parte 2: las campañas de envío (Javier y Ailén). "nueva" va antes
  // de ":id" para que no se lea como un id.
  ...[
    { path: '/campanas', page: <CampanasPage /> },
    { path: '/campanas/nueva', page: <CampanaEditorPage /> },
    { path: '/campanas/:id/editar', page: <CampanaEditorPage /> },
    { path: '/campanas/:id', page: <CampanaDetallePage /> },
  ].map(({ path, page }) => ({
    path,
    element: (
      <RequireAuth roles={['ADMINISTRADOR', 'COLABORADOR']}>
        <AppLayout>{page}</AppLayout>
      </RequireAuth>
    ),
  })),
  {
    // Gestión semanal de prioridades de Ailen (ciclo-tareas, R5/R6): sin rol.
    path: '/tareas',
    element: (
      <RequireAuth>
        <AppLayout>
          <TareasPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    // ciclo-metricas-embudo-y-contenido, R5: embudo + costos — declarada ANTES del
    // catch-all de abajo para que "indicadores" no se interprete como un leadId.
    path: '/bandeja/indicadores',
    element: (
      <RequireAuth roles={['ADMINISTRADOR', 'COLABORADOR']}>
        <AppLayout>
          <EmbudoPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/bandeja/:leadId?',
    element: (
      <RequireAuth roles={['ADMINISTRADOR', 'COLABORADOR']}>
        <AppLayout>
          <BandejaPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    // Conexiones externas (01/10/2026): mismo acceso que el Publicador, sin
    // distinción de rol entre Ailen y Javier (pedido explícito).
    path: '/conexiones',
    element: (
      <RequireAuth roles={['ADMINISTRADOR', 'COLABORADOR']}>
        <AppLayout>
          <ConexionesPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    // "Mi perfil" (cualquier rol autenticado).
    path: '/perfil',
    element: (
      <RequireAuth>
        <AppLayout>
          <MiPerfilPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/admin/usuarios',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <UsuariosPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/admin/campanas',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <AnunciosPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    // Sistema (ciclo-panel, R5): el estado del VPS, solo administración.
    path: '/admin/sistema',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <SistemaAdminPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/admin/metricas',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <MetricasAdminPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/admin/errores',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <ErroresAdminPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    path: '/admin/configuracion',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <ConfiguracionAdminPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  {
    // Gestión de API keys para integración externa.
    path: '/admin/api-keys',
    element: (
      <RequireAuth roles={['ADMINISTRADOR']}>
        <AppLayout>
          <ApiKeysPage />
        </AppLayout>
      </RequireAuth>
    ),
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
